/**
 * Shared airport reference data — IATA code, full airport name, city and
 * country, plus coordinates for nearest-airport geolocation. Previously
 * lived only inside OriginContext.tsx (as a smaller, code/city/lat/lon-only
 * list) for browser-geolocation → nearest-airport lookups; extended here
 * with `name`/`country` so it can also back an airport-picker's dropdown,
 * and moved out so both can import the same list instead of keeping two.
 */
export type Airport = {
    /** IATA code, e.g. "JFK". */
    code: string;
    /** Full airport name, e.g. "John F. Kennedy International Airport". */
    name: string;
    city: string;
    country: string;
    lat: number;
    lon: number;
};

export const AIRPORTS: Airport[] = [
    { code: 'JFK', name: 'John F. Kennedy International Airport', city: 'New York', country: 'United States', lat: 40.6413, lon: -73.7781 },
    { code: 'EWR', name: 'Newark Liberty International Airport', city: 'New York', country: 'United States', lat: 40.6895, lon: -74.1745 },
    { code: 'LAX', name: 'Los Angeles International Airport', city: 'Los Angeles', country: 'United States', lat: 33.9425, lon: -118.4081 },
    { code: 'ORD', name: "O'Hare International Airport", city: 'Chicago', country: 'United States', lat: 41.9742, lon: -87.9073 },
    { code: 'MIA', name: 'Miami International Airport', city: 'Miami', country: 'United States', lat: 25.7959, lon: -80.2870 },
    { code: 'SFO', name: 'San Francisco International Airport', city: 'San Francisco', country: 'United States', lat: 37.6213, lon: -122.3790 },
    { code: 'SEA', name: 'Seattle-Tacoma International Airport', city: 'Seattle', country: 'United States', lat: 47.4502, lon: -122.3088 },
    { code: 'BOS', name: 'Logan International Airport', city: 'Boston', country: 'United States', lat: 42.3656, lon: -71.0096 },
    { code: 'DFW', name: 'Dallas/Fort Worth International Airport', city: 'Dallas', country: 'United States', lat: 32.8998, lon: -97.0403 },
    { code: 'ATL', name: 'Hartsfield-Jackson Atlanta International Airport', city: 'Atlanta', country: 'United States', lat: 33.6407, lon: -84.4277 },
    { code: 'DEN', name: 'Denver International Airport', city: 'Denver', country: 'United States', lat: 39.8561, lon: -104.6737 },
    { code: 'LHR', name: 'Heathrow Airport', city: 'London', country: 'United Kingdom', lat: 51.4700, lon: -0.4543 },
    { code: 'LGW', name: 'Gatwick Airport', city: 'London', country: 'United Kingdom', lat: 51.1481, lon: -0.1903 },
    { code: 'STN', name: 'London Stansted Airport', city: 'London', country: 'United Kingdom', lat: 51.8860, lon: 0.2389 },
    { code: 'CDG', name: 'Charles de Gaulle Airport', city: 'Paris', country: 'France', lat: 49.0097, lon: 2.5479 },
    { code: 'NRT', name: 'Narita International Airport', city: 'Tokyo', country: 'Japan', lat: 35.7720, lon: 140.3929 },
    { code: 'HND', name: 'Haneda Airport', city: 'Tokyo', country: 'Japan', lat: 35.5494, lon: 139.7798 },
    { code: 'DXB', name: 'Dubai International Airport', city: 'Dubai', country: 'United Arab Emirates', lat: 25.2532, lon: 55.3657 },
    { code: 'SIN', name: 'Singapore Changi Airport', city: 'Singapore', country: 'Singapore', lat: 1.3644, lon: 103.9915 },
    { code: 'BCN', name: 'Barcelona–El Prat Airport', city: 'Barcelona', country: 'Spain', lat: 41.2974, lon: 2.0833 },
    { code: 'FCO', name: 'Leonardo da Vinci–Fiumicino Airport', city: 'Rome', country: 'Italy', lat: 41.8003, lon: 12.2389 },
    { code: 'AMS', name: 'Amsterdam Airport Schiphol', city: 'Amsterdam', country: 'Netherlands', lat: 52.3105, lon: 4.7683 },
    { code: 'FRA', name: 'Frankfurt Airport', city: 'Frankfurt', country: 'Germany', lat: 50.0379, lon: 8.5622 },
    { code: 'IST', name: 'Istanbul Airport', city: 'Istanbul', country: 'Turkey', lat: 41.2753, lon: 28.7519 },
    { code: 'BKK', name: 'Suvarnabhumi Airport', city: 'Bangkok', country: 'Thailand', lat: 13.6900, lon: 100.7501 },
    { code: 'SYD', name: 'Sydney Kingsford Smith Airport', city: 'Sydney', country: 'Australia', lat: -33.9461, lon: 151.1772 },
    { code: 'YYZ', name: 'Toronto Pearson International Airport', city: 'Toronto', country: 'Canada', lat: 43.6777, lon: -79.6248 },
    { code: 'ICN', name: 'Incheon International Airport', city: 'Seoul', country: 'South Korea', lat: 37.4602, lon: 126.4407 },
    { code: 'HKG', name: 'Hong Kong International Airport', city: 'Hong Kong', country: 'Hong Kong', lat: 22.3080, lon: 113.9185 },
    { code: 'MAD', name: 'Adolfo Suárez Madrid–Barajas Airport', city: 'Madrid', country: 'Spain', lat: 40.4983, lon: -3.5676 },
    { code: 'BER', name: 'Berlin Brandenburg Airport', city: 'Berlin', country: 'Germany', lat: 52.3667, lon: 13.5033 },
    { code: 'MUC', name: 'Munich Airport', city: 'Munich', country: 'Germany', lat: 48.3537, lon: 11.7750 },
    { code: 'LIS', name: 'Humberto Delgado Airport', city: 'Lisbon', country: 'Portugal', lat: 38.7756, lon: -9.1354 },
    { code: 'ATH', name: 'Athens International Airport', city: 'Athens', country: 'Greece', lat: 37.9364, lon: 23.9445 },
    { code: 'ZRH', name: 'Zurich Airport', city: 'Zurich', country: 'Switzerland', lat: 47.4647, lon: 8.5492 },
    { code: 'VIE', name: 'Vienna International Airport', city: 'Vienna', country: 'Austria', lat: 48.1103, lon: 16.5697 },
    { code: 'SOF', name: 'Sofia Airport', city: 'Sofia', country: 'Bulgaria', lat: 42.6952, lon: 23.4062 },
    { code: 'TBS', name: 'Tbilisi International Airport', city: 'Tbilisi', country: 'Georgia', lat: 41.6692, lon: 44.9547 },
    { code: 'TLV', name: 'Ben Gurion Airport', city: 'Tel Aviv', country: 'Israel', lat: 32.0055, lon: 34.8854 },
    { code: 'CUN', name: 'Cancún International Airport', city: 'Cancun', country: 'Mexico', lat: 21.0365, lon: -86.8771 },
    { code: 'MEX', name: 'Mexico City International Airport', city: 'Mexico City', country: 'Mexico', lat: 19.4361, lon: -99.0719 },
    { code: 'GRU', name: 'São Paulo/Guarulhos International Airport', city: 'São Paulo', country: 'Brazil', lat: -23.4356, lon: -46.4731 },
    { code: 'EZE', name: 'Ministro Pistarini International Airport', city: 'Buenos Aires', country: 'Argentina', lat: -34.8222, lon: -58.5358 },
    { code: 'DEL', name: 'Indira Gandhi International Airport', city: 'Delhi', country: 'India', lat: 28.5562, lon: 77.1000 },
    { code: 'BOM', name: 'Chhatrapati Shivaji Maharaj International Airport', city: 'Mumbai', country: 'India', lat: 19.0896, lon: 72.8656 },
];

const norm = (s: string) => s.toLowerCase().trim();

/**
 * Ranked airport search for an autocomplete field — code match first (the
 * way most travellers type once they know it), then city, then airport
 * name, each ranked prefix-before-substring; deduped, capped at `limit`.
 */
export function searchAirports(query: string, limit = 8): Airport[] {
    const q = norm(query);
    if (!q) return AIRPORTS.slice(0, limit);

    const score = (airport: Airport): number => {
        const code = norm(airport.code);
        const city = norm(airport.city);
        const name = norm(airport.name);
        if (code === q) return 0;
        if (code.startsWith(q)) return 1;
        if (city.startsWith(q)) return 2;
        if (name.startsWith(q)) return 3;
        if (city.includes(q)) return 4;
        if (name.includes(q)) return 5;
        if (code.includes(q)) return 6;
        return -1;
    };

    return AIRPORTS
        .map((airport) => ({ airport, rank: score(airport) }))
        .filter((entry) => entry.rank >= 0)
        .sort((a, b) => a.rank - b.rank)
        .slice(0, limit)
        .map((entry) => entry.airport);
}

export function getAirportByCode(code: string): Airport | undefined {
    const upper = code.toUpperCase();
    return AIRPORTS.find((airport) => airport.code === upper);
}
